import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDateTime, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Package, AlertTriangle, Wallet, ArrowUpRight, ArrowDownRight, Store as StoreIcon, Receipt, ShoppingBag, TrendingDown as ExpenseIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Bar } from "recharts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];

export default function Dashboard() {
  const { isOwner } = useAuth();
  const [d, setD] = useState(null);
  const [drilldown, setDrilldown] = useState(null); // "warehouse" | "consignment"
  const [detailRows, setDetailRows] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [monthDetail, setMonthDetail] = useState(null); // { year, month, kind, label }
  const [monthData, setMonthData] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearPw, setClearPw] = useState("");
  const [clearBusy, setClearBusy] = useState(false);
  const [complaintsInfo, setComplaintsInfo] = useState(null);
  const [ordersInfo, setOrdersInfo] = useState(null);
  const [opnameStatus, setOpnameStatus] = useState(null);
  const [opnameDetailOpen, setOpnameDetailOpen] = useState(false);

  const load = () => api.get("/dashboard").then((r) => setD(r.data)).catch(()=>{});
  const loadMonthly = () => isOwner && api.get("/reports/monthly").then((r) => setMonthly(r.data)).catch(()=>{});
  const loadOwnerExtras = () => {
    if (!isOwner) return;
    api.get("/dashboard/complaints-summary").then((r) => setComplaintsInfo(r.data)).catch(()=>{});
    api.get("/dashboard/orders-summary").then((r) => setOrdersInfo(r.data)).catch(()=>{});
    api.get("/admin/opname-status").then((r) => setOpnameStatus(r.data)).catch(()=>{});
  };
  useEffect(() => { load(); loadMonthly(); loadOwnerExtras(); }, [isOwner]);

  const openDrilldown = async (type) => {
    setDrilldown(type);
    try {
      const path = type === "warehouse" ? "/stock-detail/warehouse" : "/stock-detail/consignment";
      const { data } = await api.get(path);
      setDetailRows(data);
    } catch { setDetailRows([]); }
  };

  const openMonthDetail = async (year, month, kind, label) => {
    setMonthDetail({ year, month, kind, label });
    setMonthData(null);
    try {
      const { data } = await api.get(`/dashboard/monthly-detail/${year}/${month}?kind=${kind}`);
      setMonthData(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const closeMonthDetail = () => { setMonthDetail(null); setMonthData(null); };

  const doClearAll = async () => {
    if (!clearPw) return toast.error("Masukkan password owner Anda");
    setClearBusy(true);
    try {
      await api.post("/admin/clear-all-data", { password: clearPw });
      toast.success("Semua data transaksi berhasil dihapus. Master data tetap aman.");
      setClearOpen(false); setClearPw("");
      load(); loadMonthly();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setClearBusy(false); }
  };

  if (!d) return <div className="p-4 text-slate-500">Memuat...</div>;

  const stats = [
    { label: "Saldo Kas", value: formatIDR(d.balance), icon: Wallet, tone: d.balance >= 0 ? "primary" : "danger", testId:"stat-balance" },
    { label: "Nilai Stok Gudang", value: formatIDR(d.total_stock_value), icon: Package, tone: "neutral", testId:"stat-stock-value", sub: `${d.total_stock_qty} unit`, onClick: () => openDrilldown("warehouse") },
    { label: "Stok Titipan Toko", value: formatIDR(d.consignment_value || 0), icon: ArrowUpRight, tone: "neutral", testId:"stat-consignment", sub: `${d.consignment_qty || 0} unit di toko`, onClick: () => openDrilldown("consignment") },
    { label: "Penjualan Hari Ini", value: formatIDR(d.sales_today), icon: TrendingUp, tone: "success", testId:"stat-sales-today" },
    { label: "Bayaran Toko Hari Ini", value: formatIDR(d.store_sales_today || 0), icon: TrendingUp, tone: "success", testId:"stat-store-sales-today" },
    { label: "Pengeluaran Hari Ini", value: formatIDR(d.expense_today), icon: TrendingDown, tone: "danger", testId:"stat-expense-today" },
  ];

  const chartData = d.chart_7d.map(x => ({
    day: new Date(x.date).toLocaleDateString("id-ID",{ day:"2-digit", month:"short"}),
    Masuk: x.in, Keluar: x.out,
  }));

  // Bar chart untuk monthly (last 6)
  const monthlyBar = monthly.slice(-6).map(m => ({
    name: m.label,
    Langsung: m.revenue_direct,
    "Toko Binaan": m.revenue_store,
    Beban: m.expense_total,
  }));

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Ringkasan operasional Project Hotto"
        testId="page-dashboard"
      />

      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {stats.map((s) => (
          <Card
            key={s.label}
            className={`stat-card p-5 border border-[var(--hotto-border)] rounded-md ${s.onClick ? "cursor-pointer hover:border-[var(--hotto-primary)]" : ""}`}
            data-testid={s.testId}
            onClick={s.onClick}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="overline">{s.label}</div>
              <s.icon className={`w-4 h-4 ${
                s.tone === "success" ? "text-[var(--hotto-success)]" :
                s.tone === "danger" ? "text-[var(--hotto-destructive)]" :
                s.tone === "primary" ? "text-[var(--hotto-primary)]" : "text-slate-500"
              }`} />
            </div>
            <div className="font-mono-num text-xl font-semibold tracking-tight">{s.value}</div>
            {s.sub && <div className="text-xs text-[var(--hotto-muted)] mt-1">{s.sub}{s.onClick && " · klik untuk detail"}</div>}
          </Card>
        ))}
      </div>

      {/* ===== Owner extras: keluhan mitra + status opname ===== */}
      {isOwner && (ordersInfo || complaintsInfo || opnameStatus) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          {ordersInfo && (
            <Card className="p-5 border border-[var(--hotto-border)] rounded-md" data-testid="widget-orders">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="overline">Pesanan Mitra Menunggu</div>
                  <div className="text-2xl font-bold font-mono-num mt-1 text-amber-700">
                    {ordersInfo.pending_count}
                    <span className="ml-2 text-sm text-slate-500 font-normal">
                      ({ordersInfo.approved_count} disetujui)
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => window.location.href = "/admin/orders"}
                  className="text-xs text-[var(--hotto-primary)] hover:underline"
                  data-testid="link-orders"
                >
                  Review →
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(ordersInfo.recent || []).slice(0, 5).map((order) => (
                  <div key={order.id} className="text-xs border-l-2 border-amber-200 pl-2 py-1">
                    <div className="font-medium truncate">{order.store_name}</div>
                    <div className="text-slate-500 truncate">
                      {(order.items || []).map((item) => `${item.product_name}×${item.qty}`).join(", ")}
                    </div>
                  </div>
                ))}
                {(ordersInfo.recent || []).length === 0 && (
                  <div className="text-xs text-slate-400 italic">Tidak ada pesanan menunggu.</div>
                )}
              </div>
            </Card>
          )}
          {complaintsInfo && (
            <Card className="p-5 border border-[var(--hotto-border)] rounded-md" data-testid="widget-complaints">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="overline">Keluhan Mitra Aktif</div>
                  <div className="text-2xl font-bold font-mono-num mt-1">
                    {complaintsInfo.open_count}
                    {complaintsInfo.urgent_count > 0 && (
                      <span className="ml-2 text-sm text-red-600 font-normal">({complaintsInfo.urgent_count} urgent)</span>
                    )}
                  </div>
                </div>
                <button onClick={() => window.location.href = "/super-admin"} className="text-xs text-[var(--hotto-primary)] hover:underline" data-testid="link-complaints">
                  Kelola →
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(complaintsInfo.recent || []).slice(0, 5).map((c) => (
                  <div key={c.id} className="text-xs border-l-2 border-slate-200 pl-2 py-1">
                    <div className="font-medium truncate">{c.store_name} · {c.subject}</div>
                    <div className="text-slate-500 truncate">{c.category} · {c.priority}</div>
                  </div>
                ))}
                {(complaintsInfo.recent || []).length === 0 && <div className="text-xs text-slate-400 italic">Tidak ada keluhan aktif.</div>}
              </div>
            </Card>
          )}
          {opnameStatus && (
            <Card
              className="p-5 border border-[var(--hotto-border)] rounded-md cursor-pointer hover:border-[var(--hotto-primary)] transition-colors"
              onClick={() => setOpnameDetailOpen(true)}
              data-testid="widget-opname"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="overline">Status Opname Bulan {opnameStatus.month}</div>
                  <div className="text-2xl font-bold font-mono-num mt-1">
                    <span className="text-emerald-700">{opnameStatus.submitted}</span>
                    <span className="text-slate-400 text-sm mx-1">/</span>
                    <span className="text-slate-600">{opnameStatus.total_stores}</span>
                    <span className="text-xs font-normal text-slate-500 ml-1">toko</span>
                  </div>
                </div>
                <div className="text-xs text-slate-500 text-right">
                  <div className="text-amber-700 font-semibold">{opnameStatus.pending} belum</div>
                  <div className="mt-1">klik untuk detail</div>
                </div>
              </div>
              <div className="w-full bg-slate-100 rounded h-2 overflow-hidden">
                <div className="bg-emerald-500 h-full" style={{ width: `${(opnameStatus.submitted / Math.max(1, opnameStatus.total_stores)) * 100}%` }}/>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ===== Laporan Bulanan (owner only) ===== */}
      {isOwner && monthly.length > 0 && (
        <Card className="p-6 border border-[var(--hotto-border)] rounded-md mb-8" data-testid="monthly-report-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="overline mb-1">Laporan Bulanan</div>
              <h3 className="text-lg font-semibold" style={{fontFamily:'Outfit'}}>Penjualan &amp; Pengeluaran per Bulan</h3>
              <p className="text-xs text-slate-500 mt-1">Klik angka pada tabel untuk melihat detail transaksinya.</p>
            </div>
          </div>

          {/* Chart 6 bulan terakhir */}
          <div className="h-64 mb-6">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 800, height: 256 }}>
              <BarChart data={monthlyBar}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                <XAxis dataKey="name" stroke="#64748b" fontSize={11}/>
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v)=>`${(v/1000000).toFixed(1)}jt`}/>
                <Tooltip formatter={(v)=>formatIDR(v)}/>
                <Legend/>
                <Bar dataKey="Langsung" fill="#047857" radius={[4,4,0,0]}/>
                <Bar dataKey="Toko Binaan" fill="#0284c7" radius={[4,4,0,0]}/>
                <Bar dataKey="Beban" fill="#EA580C" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <MonthlyTable rows={monthly} onOpen={openMonthDetail}/>
        </Card>
      )}

      {/* Chart + Low stock */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <Card className="lg:col-span-2 p-6 border border-[var(--hotto-border)] rounded-md">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline mb-1">7 Hari Terakhir</div>
              <h3 className="text-lg font-semibold" style={{fontFamily:'Outfit'}}>Kas Masuk vs Kas Keluar</h3>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 800, height: 256 }}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                <XAxis dataKey="day" stroke="#64748b" fontSize={12}/>
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v)=>`${(v/1000).toFixed(0)}k`}/>
                <Tooltip formatter={(v)=>formatIDR(v)} />
                <Legend />
                <Line type="monotone" dataKey="Masuk" stroke="#047857" strokeWidth={2} dot={{r:3}}/>
                <Line type="monotone" dataKey="Keluar" stroke="#EA580C" strokeWidth={2} dot={{r:3}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Peringatan</div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Outfit'}}>
            <AlertTriangle className="w-4 h-4 text-[var(--hotto-alert)]" />
            Stok Menipis
          </h3>

          <div className="mb-4">
            <div className="text-xs uppercase font-semibold text-slate-500 mb-2">Gudang (≤ 5 unit)</div>
            {d.low_stock.length === 0 ? (
              <div className="text-xs text-slate-400 py-2">Semua aman.</div>
            ) : (
              <div className="divide-y divide-[var(--hotto-border)]">
                {d.low_stock.map((p) => (
                  <div key={p.id} className="py-2 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-xs text-[var(--hotto-muted)]">{p.sku || "-"}</div>
                    </div>
                    <Badge variant="outline" className="border-[var(--hotto-alert)] text-[var(--hotto-alert)] font-mono-num">
                      {p.stock} {p.unit || "pcs"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs uppercase font-semibold text-slate-500 mb-2 flex items-center gap-1">
              <StoreIcon className="w-3 h-3"/> Retail (&lt; 10 unit)
            </div>
            {(!d.low_stock_retail || d.low_stock_retail.length === 0) ? (
              <div className="text-xs text-slate-400 py-2">Semua retail aman.</div>
            ) : (
              <div className="divide-y divide-[var(--hotto-border)] max-h-56 overflow-y-auto">
                {d.low_stock_retail.map((r, i) => (
                  <div key={i} className="py-2 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium truncate max-w-[180px]">{r.store_name}</div>
                      <div className="text-xs text-[var(--hotto-muted)]">{r.product_name}</div>
                    </div>
                    <Badge variant="outline" className="border-orange-400 text-orange-700 font-mono-num shrink-0">
                      {r.qty} {r.unit}
                    </Badge>
                  </div>
                ))}
                {d.low_stock_retail_count > d.low_stock_retail.length && (
                  <div className="py-2 text-xs text-slate-400 italic">+ {d.low_stock_retail_count - d.low_stock_retail.length} entri lain</div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Recent CF */}
      <Card className="p-6 border border-[var(--hotto-border)] rounded-md">
        <div className="overline mb-1">Aktivitas Terkini</div>
        <h3 className="text-lg font-semibold mb-4" style={{fontFamily:'Outfit'}}>Riwayat Arus Kas</h3>
        <div className="divide-y divide-[var(--hotto-border)]">
          {d.recent_cashflow.length === 0 ? (
            <div className="text-sm text-[var(--hotto-muted)] py-6 text-center">Belum ada transaksi.</div>
          ) : d.recent_cashflow.map((c) => (
            <div key={c.id} className="py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-md flex items-center justify-center ${c.type === "in" ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}>
                  {c.type === "in" ? <ArrowUpRight className="w-4 h-4"/> : <ArrowDownRight className="w-4 h-4"/>}
                </div>
                <div>
                  <div className="text-sm font-medium">{c.description}</div>
                  <div className="text-xs text-[var(--hotto-muted)]">{c.category} · {formatDateTime(c.date)}</div>
                </div>
              </div>
              <div className={`font-mono-num font-semibold ${c.type === "in" ? "text-[var(--hotto-success)]" : "text-[var(--hotto-destructive)]"}`}>
                {c.type === "in" ? "+" : "-"} {formatIDR(c.amount)}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Drilldown: Stok */}
      <Dialog open={!!drilldown} onOpenChange={(v)=>!v && setDrilldown(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {drilldown === "warehouse" ? "Detail Stok Gudang" : "Detail Stok Titipan di Toko"}
            </DialogTitle>
          </DialogHeader>
          {detailRows.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">Tidak ada data.</div>
          ) : drilldown === "warehouse" ? (
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Produk</TableHead><TableHead>SKU</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Harga Beli</TableHead><TableHead className="text-right">Nilai</TableHead></TableRow></TableHeader>
              <TableBody>
                {detailRows.map((r)=>(
                  <TableRow key={r.id} className="table-row-hover">
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-slate-500 text-xs font-mono-num">{r.sku || "-"}</TableCell>
                    <TableCell className="text-right font-mono-num">{r.qty} {r.unit}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-500">{formatIDR(r.purchase_price)}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{formatIDR(r.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Toko</TableHead><TableHead>Produk</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Harga Toko</TableHead><TableHead className="text-right">Nilai</TableHead></TableRow></TableHeader>
              <TableBody>
                {detailRows.map((r,i)=>(
                  <TableRow key={i} className="table-row-hover">
                    <TableCell className="font-medium">{r.store_name}</TableCell>
                    <TableCell>{r.product_name} <span className="text-xs text-slate-400 font-mono-num">{r.sku||""}</span></TableCell>
                    <TableCell className="text-right font-mono-num">{r.qty} {r.unit}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-500">{formatIDR(r.unit_price)}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{formatIDR(r.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Drilldown: Monthly Detail */}
      <Dialog open={!!monthDetail} onOpenChange={(v)=>!v && closeMonthDetail()}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="month-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {monthDetail ? (
                <>Detail {monthDetail.kind === "sales" ? "Penjualan Konsumen" :
                          monthDetail.kind === "store_sales" ? "Pembayaran dari Toko" :
                          "Pengeluaran"} — {monthDetail.label}</>
              ) : "Detail"}
            </DialogTitle>
          </DialogHeader>
          {!monthDetail ? null : !monthData ? (
            <div className="text-center py-8 text-slate-500">Memuat...</div>
          ) : (
            <MonthDetailContent monthDetail={monthDetail} monthData={monthData}/>
          )}
        </DialogContent>
      </Dialog>

      {/* Opname Detail Dialog */}
      <Dialog open={opnameDetailOpen} onOpenChange={setOpnameDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="opname-detail-dialog">
          <DialogHeader><DialogTitle>Detail Status Opname Bulan {opnameStatus?.month}</DialogTitle></DialogHeader>
          {opnameStatus && (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Toko</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tanggal Opname</TableHead>
                <TableHead>Oleh</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(opnameStatus.stores || []).map((s) => (
                  <TableRow key={s.store_id}>
                    <TableCell className="font-medium">{s.store_name}</TableCell>
                    <TableCell>
                      {s.submitted
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">✓ Sudah</Badge>
                        : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">⌛ Belum</Badge>}
                    </TableCell>
                    <TableCell className="font-mono-num text-sm">{s.opname_date ? formatDate(s.opname_date) : "-"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{s.submitted_by || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ==== Monthly Table Component ==== */
function MonthlyTable({ rows, onOpen }) {
  const tf = useTableFilter(rows, {
    searchKeys: ["label"],
    defaultSort: { key: "key", dir: "desc" },
  });
  return (
    <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
      <div className="p-3 border-b border-[var(--hotto-border)]">
        <TableToolbar tf={tf} placeholder="Cari bulan..." testId="monthly-search"/>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <SortHeader tf={tf} col="key">Bulan</SortHeader>
            <SortHeader tf={tf} col="revenue_direct" className="text-right">Jual Langsung</SortHeader>
            <SortHeader tf={tf} col="revenue_store" className="text-right">Bayaran Toko</SortHeader>
            <SortHeader tf={tf} col="revenue" className="text-right">Total Penjualan</SortHeader>
            <SortHeader tf={tf} col="expense_total" className="text-right">Pengeluaran</SortHeader>
            <SortHeader tf={tf} col="net_profit" className="text-right">Laba Bersih</SortHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tf.rows.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada data bulanan.</TableCell></TableRow>
          )}
          {tf.rows.map((m) => (
            <TableRow key={m.key} className="table-row-hover" data-testid={`monthly-row-${m.key}`}>
              <TableCell className="font-semibold" style={{fontFamily:'Outfit'}}>{m.label}</TableCell>
              <TableCell className="text-right">
                <button
                  onClick={() => onOpen(m.year, m.month, "sales", m.label)}
                  className="font-mono-num text-emerald-700 hover:underline hover:text-emerald-800 cursor-pointer"
                  data-testid={`open-sales-${m.key}`}
                  title="Klik untuk detail"
                >
                  {formatIDR(m.revenue_direct)}
                  <div className="text-[10px] text-slate-400 font-sans">{m.sales_count}x</div>
                </button>
              </TableCell>
              <TableCell className="text-right">
                <button
                  onClick={() => onOpen(m.year, m.month, "store_sales", m.label)}
                  className="font-mono-num text-sky-700 hover:underline hover:text-sky-800 cursor-pointer"
                  data-testid={`open-storesales-${m.key}`}
                  title="Klik untuk detail"
                >
                  {formatIDR(m.revenue_store)}
                  <div className="text-[10px] text-slate-400 font-sans">{m.store_sales_count}x</div>
                </button>
              </TableCell>
              <TableCell className="text-right font-mono-num text-slate-700 font-semibold">{formatIDR(m.revenue)}</TableCell>
              <TableCell className="text-right">
                <button
                  onClick={() => onOpen(m.year, m.month, "expenses", m.label)}
                  className="font-mono-num text-orange-700 hover:underline hover:text-orange-800 cursor-pointer"
                  data-testid={`open-expenses-${m.key}`}
                  title="Klik untuk detail"
                >
                  {formatIDR(m.expense_total)}
                </button>
              </TableCell>
              <TableCell className="text-right font-mono-num font-bold">
                <Badge className={m.net_profit>=0?"bg-emerald-50 text-emerald-700 hover:bg-emerald-50":"bg-red-50 text-red-700 hover:bg-red-50"}>
                  {formatIDR(m.net_profit)}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/* ==== Month Detail (drilldown content) ==== */
function MonthDetailContent({ monthDetail, monthData }) {
  const kind = monthDetail?.kind;
  const rows = (kind && monthData?.[kind]?.rows) || [];
  const total = (kind && monthData?.[kind]?.total) || 0;
  const count = (kind && monthData?.[kind]?.count) || 0;

  const tf = useTableFilter(rows, {
    searchKeys: kind === "sales" ? ["customer_name", "items_text"] :
                kind === "store_sales" ? ["store_name", "items_text"] :
                kind === "expenses" ? ["category", "description"] : [],
    defaultSort: { key: "date", dir: "desc" },
  });

  const byCategory = monthData?.expenses?.by_category || [];

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-1">Total</div>
          <div className="font-mono-num text-2xl font-bold text-[var(--hotto-primary)]">{formatIDR(total)}</div>
          <div className="text-xs text-slate-500 mt-1">{count} transaksi</div>
        </Card>
        {kind === "expenses" && byCategory.length > 0 && (
          <Card className="p-4 border border-[var(--hotto-border)] rounded-md">
            <div className="overline mb-2">Top Kategori</div>
            <div className="space-y-1">
              {byCategory.slice(0, 3).map((c) => (
                <div key={c.category} className="flex justify-between text-xs">
                  <span className="text-slate-600">{c.category}</span>
                  <span className="font-mono-num font-semibold">{formatIDR(c.amount)}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      <div>
        <TableToolbar tf={tf} placeholder="Cari..." testId="month-detail-search"/>
      </div>

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <SortHeader tf={tf} col="date">Tanggal</SortHeader>
              {kind === "sales" && <SortHeader tf={tf} col="customer_name">Konsumen</SortHeader>}
              {kind === "store_sales" && <SortHeader tf={tf} col="store_name">Toko</SortHeader>}
              {kind === "expenses" && <SortHeader tf={tf} col="category">Kategori</SortHeader>}
              {kind !== "expenses" && <TableHead>Item</TableHead>}
              {kind === "expenses" && <SortHeader tf={tf} col="description">Deskripsi</SortHeader>}
              {kind !== "expenses" && <SortHeader tf={tf} col="qty" className="text-right">Qty</SortHeader>}
              <SortHeader tf={tf} col={kind === "expenses" ? "amount" : "total"} className="text-right">Nominal</SortHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tf.rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">Tidak ada transaksi.</TableCell></TableRow>}
            {tf.rows.map((r) => (
              <TableRow key={r.id} className="table-row-hover">
                <TableCell className="font-mono-num text-xs">{formatDate(r.date)}</TableCell>
                {kind === "sales" && <TableCell className="font-medium">{r.customer_name || "-"}</TableCell>}
                {kind === "store_sales" && <TableCell className="font-medium">{r.store_name}</TableCell>}
                {kind === "expenses" && <TableCell><Badge variant="outline" className="uppercase text-[10px]">{r.category}</Badge></TableCell>}
                {kind !== "expenses" && <TableCell className="text-xs text-slate-600 max-w-md truncate">{r.items_text}</TableCell>}
                {kind === "expenses" && <TableCell className="text-slate-600 text-sm">{r.description || "-"}</TableCell>}
                {kind !== "expenses" && <TableCell className="text-right font-mono-num">{r.qty}</TableCell>}
                <TableCell className="text-right font-mono-num font-semibold">{formatIDR(kind === "expenses" ? r.amount : r.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
